
import { defineComponent, PropType, ref, computed, watch } from 'vue'
import VModal from '../../base/modal/VModal.vue'
import VButton from '../../base/button/VButton.vue'

interface ProductLine {
  is_key_contract: boolean
  product: number
  price_ratio: number
  trade_ratio: number
  transaction_type?: string
}

interface Product {
  id: number
  name: string
  product: string
}

interface CalculatedVolume {
  productName: string
  exeRatio: number
  volume: number
  transactionType: string
  isKey: boolean
  productId: number
}

export default defineComponent({
  name: 'LegVolumesCalculatorModal',
  components: {
    VModal,
    VButton
  },
  props: {
    open: {
      type: Boolean,
      default: false
    },
    productLines: {
      type: Array as PropType<ProductLine[]>,
      default: () => []
    },
    products: {
      type: Array as PropType<Product[]>,
      default: () => []
    },
    initialVolume: {
      type: Number,
      default: 100
    }
  },
  emits: ['close', 'apply'],
  setup(props, { emit }) {
    const keyProductVolume = ref<number>(props.initialVolume)
    const copiedIndex = ref<number | null>(null)
    const copiedAll = ref<boolean>(false)
    
    // Watch for prop changes to update the volume when modal opens
    watch(() => props.open, (newVal) => {
      if (newVal && props.initialVolume) {
        keyProductVolume.value = props.initialVolume
      }
    })
    
    watch(() => props.initialVolume, (newVal) => {
      if (newVal && props.open) {
        keyProductVolume.value = newVal
      }
    })
    
    const calculatedVolumes = computed((): CalculatedVolume[] => {
      if (!keyProductVolume.value || keyProductVolume.value <= 0) {
        return []
      }

      return props.productLines.map(line => {
        const product = props.products.find(p => p.id === line.product)
        // Use product.product or product.name for the display name
        const productName = product ? (product.product || product.name || `Product ${line.product}`) : `Product ${line.product}`
        const exeRatio = line.trade_ratio || 0
        const volume = Math.round(keyProductVolume.value * exeRatio)
        const transactionType = exeRatio >= 0 ? 'BUY' : 'SELL'

        return {
          productName,
          exeRatio,
          volume,
          transactionType,
          isKey: line.is_key_contract,
          productId: line.product
        }
      })
    })

    const calculateVolumes = () => {
      // Trigger recalculation by updating the computed property dependencies
    }

    const copyToClipboard = async (value: number, index: number) => {
      try {
        await navigator.clipboard.writeText(value.toString())
        copiedIndex.value = index
        setTimeout(() => {
          copiedIndex.value = null
        }, 2000)
      } catch (err) {
        console.error('Failed to copy: ', err)
      }
    }

    const copyAllVolumes = async () => {
      try {
        // ASCII table format with row separators
        const maxProductLength = Math.max(
          8, // "Contract" length
          ...calculatedVolumes.value.map(item => item.productName.length)
        )
        const maxVolumeLength = Math.max(
          7, // "Volume" length
          ...calculatedVolumes.value.map(item => item.volume.toString().length)
        )
        
        const separator = `+${'-'.repeat(maxProductLength + 2)}+${'-'.repeat(maxVolumeLength + 2)}+`
        const header = `| ${'Contract'.padEnd(maxProductLength)} | ${'Volume'.padStart(maxVolumeLength)} |`
        
        const rows = calculatedVolumes.value
          .map((item, index) => {
            const row = `| ${item.productName.padEnd(maxProductLength)} | ${item.volume.toString().padStart(maxVolumeLength)} |`
            // Add separator after each row
            return `${row}\n${separator}`
          })
          .join('\n')
        
        const tableText = `${separator}\n${header}\n${separator}\n${rows}`
        
        await navigator.clipboard.writeText(tableText)
        copiedAll.value = true
        setTimeout(() => {
          copiedAll.value = false
        }, 2000)
      } catch (err) {
        console.error('Failed to copy all: ', err)
      }
    }

    const applyVolumes = () => {
      if (keyProductVolume.value && keyProductVolume.value > 0) {
        const keyProduct = calculatedVolumes.value.find(v => v.isKey)
        
        emit('apply', {
          keyVolume: keyProductVolume.value,
          volumes: calculatedVolumes.value,
          keyProductName: keyProduct?.productName || 'Unknown'
        })
        emit('close')
      }
    }

    return {
      keyProductVolume,
      calculatedVolumes,
      copiedIndex,
      copiedAll,
      calculateVolumes,
      copyToClipboard,
      copyAllVolumes,
      applyVolumes
    }
  }
})
